import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Ban,
  CalendarClock,
  CalendarDays,
  CalendarPlus,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  DoorOpen,
  MoreHorizontal,
  Plus,
  User,
  UserX,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import type { AppointmentStatus } from "@rn/contracts";
import {
  AppointmentDetailDialog,
  AppointmentStatusBadge,
  appointmentEncerrado,
  dataPorExtenso,
  NewAppointmentDialog,
  RescheduleAppointmentDialog,
  type Appointment,
  type Professional,
} from "@/components/crm/AppointmentDialog";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Hint } from "@/components/ui/tooltip";
import { api, ApiError } from "@/lib/api";
import { formatCurrency, horaCurta, localDateInput } from "@/lib/crm";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/agenda")({
  component: AgendaPage,
});

type Visao = "dia" | "semana";
type Transicao = "confirmed" | "arrived" | "completed" | "no_show";

/** Meia-noite de hoje, no relógio do navegador. */
function hoje(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Segunda-feira da semana da data. */
function inicioDaSemana(date: Date): Date {
  const d = new Date(date);
  const dia = d.getDay();
  d.setDate(d.getDate() - ((dia + 6) % 7));
  d.setHours(0, 0, 0, 0);
  return d;
}

const somaDias = (date: Date, dias: number) => {
  const d = new Date(date);
  d.setDate(d.getDate() + dias);
  return d;
};

const diaSemana = (d: Date) =>
  new Intl.DateTimeFormat("pt-BR", { weekday: "short" }).format(d).replace(".", "");

const mesPorExtenso = (d: Date) => new Intl.DateTimeFormat("pt-BR", { month: "long" }).format(d);

/** "22 a 28 de setembro" ou, virando o mês, "28 de setembro a 4 de outubro". */
function intervaloDaSemana(inicio: Date, fim: Date): string {
  if (inicio.getMonth() === fim.getMonth()) {
    return `${inicio.getDate()} a ${fim.getDate()} de ${mesPorExtenso(inicio)}`;
  }
  return `${inicio.getDate()} de ${mesPorExtenso(inicio)} a ${fim.getDate()} de ${mesPorExtenso(fim)}`;
}

/**
 * Faixa de hora do cartão, na cor do status. É o que o olho lê primeiro ao
 * correr a lista: hora e situação, antes do nome.
 */
const FAIXA: Record<AppointmentStatus, string> = {
  scheduled: "bg-info/12 text-info",
  confirmed: "bg-success/12 text-success",
  completed: "bg-success text-success-foreground",
  cancelled: "bg-muted text-muted-foreground",
  no_show: "bg-destructive/10 text-destructive",
  rescheduled: "bg-warning/18 text-warning-foreground dark:text-warning",
};

/**
 * Agenda.
 *
 * Tela de altura fixa: o cabeçalho é uma linha (navegação de data, filtro,
 * botão de agendar) e o que rola é a lista do dia — ou, na semana, cada
 * coluna por dentro. A grade de horas foi trocada pela lista porque, com meia
 * dúzia de atendimentos por dia, a grade gastava a tela com vazio e o nome
 * da cliente não cabia no bloco.
 *
 * Remarcar, confirmar, chegada, conclusão e falta ficam no menu de cada
 * cartão; cancelar abre a ficha, porque pede motivo. A remarcação encadeia o
 * agendamento novo no original em vez de mudar a data no mesmo registro — é
 * o servidor que faz isso, e é o que permite à clínica enxergar quem remarca
 * sempre.
 */
function AgendaPage() {
  const queryClient = useQueryClient();

  const [visao, setVisao] = useState<Visao>("dia");
  const [referencia, setReferencia] = useState(hoje);
  // Lido uma vez por montagem: `new Date()` na renderização é impuro, e a
  // marca de "hoje" não precisa acompanhar a virada da meia-noite.
  const [hojeStr] = useState(() => localDateInput(new Date()));
  const [filtro, setFiltro] = useState("");
  const [aberto, setAberto] = useState<Appointment | null>(null);
  const [remarcando, setRemarcando] = useState<Appointment | null>(null);
  const [novo, setNovo] = useState<{
    open: boolean;
    professionalId?: string;
    date?: string;
    time?: string;
  }>({ open: false });

  const professionals = useQuery({
    queryKey: ["professionals"],
    queryFn: () => api.get<Professional[]>("/professionals"),
  });

  const dias =
    visao === "dia"
      ? [referencia]
      : Array.from({ length: 7 }, (_, i) => somaDias(inicioDaSemana(referencia), i));

  // Vão os DIAS do calendário, e quem os transforma em horário é o servidor,
  // no fuso da clínica. Mandar a meia-noite do navegador como instante fazia
  // a lista cortar a noite (ou puxar o dia seguinte) para quem abre a agenda
  // com o relógio em outro fuso. A navegação dia/semana é desta tela, e não o
  // período global: agenda se olha por dia, não por "últimos 30 dias".
  const deDia = localDateInput(dias[0]!);
  const ateDia = localDateInput(dias[dias.length - 1]!);

  const appointments = useQuery({
    queryKey: ["appointments", deDia, ateDia, filtro],
    queryFn: () =>
      api.get<Appointment[]>("/appointments", {
        preset: "custom",
        from: deDia,
        to: ateDia,
        professionalId: filtro || undefined,
      }),
  });

  const remarcar = useMutation({
    mutationFn: (input: { id: string; scheduledAt: string }) =>
      api.post(`/appointments/${input.id}/reschedule`, { scheduledAt: input.scheduledAt }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["appointments"] });
      toast.success("Agendamento remarcado");
      setRemarcando(null);
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : "Não foi possível remarcar"),
  });

  // As transições rápidas (sem motivo) do menu do cartão. Cancelar não passa
  // por aqui: abre a ficha, que pede o motivo.
  const transitar = useMutation({
    mutationFn: (input: { id: string; to: Transicao }) =>
      api.post(`/appointments/${input.id}/transition`, { to: input.to }),
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({ queryKey: ["appointments"] });
      toast.success(input.to === "no_show" ? "Marcado como falta" : "Agendamento atualizado");
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : "Não foi possível atualizar"),
  });

  const ativos = (professionals.data ?? []).filter((p) => p.active);

  // Em ordem de horário: o servidor devolve o período inteiro, e a lista do
  // dia se lê de cima para baixo como a recepção vive o dia.
  const ordenados = useMemo(
    () =>
      [...(appointments.data ?? [])].sort(
        (a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime(),
      ),
    [appointments.data],
  );

  const doDia = (dia: Date) => {
    const chave = localDateInput(dia);
    return ordenados.filter((a) => localDateInput(new Date(a.scheduledAt)) === chave);
  };

  // Cancelado e remarcado não contam: o horário deixou de existir.
  const resumo = useMemo(() => {
    const vivos = ordenados.filter((a) => a.status !== "cancelled" && a.status !== "rescheduled");
    return {
      marcados: vivos.length,
      confirmados: vivos.filter((a) => a.status === "confirmed" || a.status === "completed").length,
      faltas: vivos.filter((a) => a.status === "no_show").length,
    };
  }, [ordenados]);

  const contemHoje = dias.some((d) => localDateInput(d) === hojeStr);
  const titulo =
    visao === "dia" ? dataPorExtenso(referencia) : intervaloDaSemana(dias[0]!, dias[6]!);
  const passo = visao === "dia" ? 1 : 7;

  const abrirNovo = (dia: Date, professionalId?: string) =>
    setNovo({ open: true, professionalId, date: localDateInput(dia) });

  const carregando = professionals.isPending || appointments.isPending;

  const acoes = {
    onOpen: (a: Appointment) => setAberto(a),
    onReschedule: (a: Appointment) => setRemarcando(a),
    onTransition: (a: Appointment, to: Transicao) => transitar.mutate({ id: a.id, to }),
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Cabeçalho de uma linha: título, navegação de data, números e ferramentas. */}
      <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b px-4 py-3 sm:px-6">
        <h1 className="font-display text-xl font-semibold tracking-tight">Agenda</h1>

        <div className="flex items-center gap-1.5">
          <Hint label={visao === "dia" ? "Dia anterior" : "Semana anterior"} side="bottom">
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={visao === "dia" ? "Dia anterior" : "Semana anterior"}
              onClick={() => setReferencia((d) => somaDias(d, -passo))}
            >
              <ChevronLeft />
            </Button>
          </Hint>
          <Button
            variant={contemHoje ? "soft" : "outline"}
            size="sm"
            onClick={() => setReferencia(hoje())}
          >
            Hoje
          </Button>
          <Hint label={visao === "dia" ? "Próximo dia" : "Próxima semana"} side="bottom">
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={visao === "dia" ? "Próximo dia" : "Próxima semana"}
              onClick={() => setReferencia((d) => somaDias(d, passo))}
            >
              <ChevronRight />
            </Button>
          </Hint>
          <span className="ml-1 text-sm font-medium">{titulo}</span>
        </div>

        {appointments.data && resumo.marcados > 0 && (
          <div className="hidden items-center gap-1.5 text-xs text-muted-foreground md:flex">
            <span className="kpi text-sm text-foreground">{resumo.marcados}</span>{" "}
            {resumo.marcados === 1 ? "marcado" : "marcados"}
            <span className="mx-1 text-border">·</span>
            <span className="kpi text-sm text-success">{resumo.confirmados}</span>{" "}
            {resumo.confirmados === 1 ? "confirmado" : "confirmados"}
            {resumo.faltas > 0 && (
              <>
                <span className="mx-1 text-border">·</span>
                <span className="kpi text-sm text-destructive">{resumo.faltas}</span>{" "}
                {resumo.faltas === 1 ? "falta" : "faltas"}
              </>
            )}
          </div>
        )}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Tabs variant="pills" defaultValue="dia" onValueChange={(v) => setVisao(v as Visao)}>
            <TabsList>
              <TabsTrigger value="dia">Dia</TabsTrigger>
              <TabsTrigger value="semana">Semana</TabsTrigger>
            </TabsList>
          </Tabs>
          <Select
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            aria-label="Profissional"
            className="w-44"
          >
            <option value="">Todas as profissionais</option>
            {ativos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
          <Button
            onClick={() => abrirNovo(referencia, filtro || undefined)}
            aria-label="Novo agendamento"
          >
            <Plus />
            <span className="hidden sm:inline">Novo agendamento</span>
          </Button>
        </div>
      </div>

      {carregando ? (
        visao === "dia" ? (
          <div className="mx-auto w-full max-w-3xl space-y-2 px-4 py-4 sm:px-6">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-[4.25rem] rounded-xl" />
            ))}
          </div>
        ) : (
          <div className="flex flex-1 gap-3 overflow-hidden px-4 py-4 sm:px-6">
            {Array.from({ length: 7 }, (_, i) => (
              <Skeleton key={i} className="h-full w-44 shrink-0 rounded-2xl" />
            ))}
          </div>
        )
      ) : ativos.length === 0 ? (
        <EmptyState
          icon={CalendarDays}
          title="Nenhuma profissional cadastrada"
          description="Cadastre em Configurações › Agenda para começar a marcar horários."
          action={
            <Button asChild variant="outline" size="sm">
              <Link to="/configuracoes">Abrir configurações</Link>
            </Button>
          }
          className="flex-1"
        />
      ) : visao === "dia" ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          {doDia(referencia).length === 0 ? (
            <EmptyState
              icon={CalendarPlus}
              title="Nada marcado para este dia"
              description={
                filtro
                  ? "Nenhum horário desta profissional. Limpe o filtro para ver a agenda toda."
                  : "Um dia livre: aproveite para encaixar quem está esperando retorno."
              }
              action={
                <Button size="sm" onClick={() => abrirNovo(referencia, filtro || undefined)}>
                  <Plus />
                  Novo agendamento
                </Button>
              }
              className="h-full"
            />
          ) : (
            <ol className="mx-auto max-w-3xl space-y-2 px-4 py-4 sm:px-6">
              {doDia(referencia).map((a) => (
                <li key={a.id}>
                  <AppointmentCard appointment={a} {...acoes} />
                </li>
              ))}
            </ol>
          )}
        </div>
      ) : (
        /* Semana: uma coluna por dia, cada uma rolando por dentro. No celular
           as colunas viram páginas com encaixe, uma por vez. */
        <div className="min-h-0 flex-1 overflow-x-auto overflow-y-hidden px-4 pb-4 pt-3 sm:px-6">
          <div className="flex h-full snap-x snap-mandatory gap-3 md:snap-none">
            {dias.map((dia) => {
              const itens = doDia(dia);
              const ehHoje = localDateInput(dia) === hojeStr;
              return (
                <section
                  key={dia.toISOString()}
                  aria-label={dataPorExtenso(dia)}
                  className={cn(
                    "flex h-full w-[78vw] shrink-0 snap-start flex-col rounded-2xl border bg-muted/40",
                    "md:w-auto md:min-w-[10.5rem] md:flex-1 md:basis-0",
                    ehHoje && "border-primary/40",
                  )}
                >
                  <header className="flex shrink-0 items-center gap-2 px-3 pb-2 pt-3">
                    <span className="text-[13px] font-semibold capitalize">{diaSemana(dia)}</span>
                    <span
                      className={cn(
                        "kpi inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-xs font-semibold",
                        ehHoje ? "bg-primary text-primary-foreground" : "bg-card shadow-card",
                      )}
                    >
                      {dia.getDate()}
                    </span>
                    {itens.length > 0 && (
                      <span className="kpi text-[11px] text-muted-foreground">{itens.length}</span>
                    )}
                    <Hint label="Agendar neste dia" side="bottom">
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label={`Agendar em ${dataPorExtenso(dia)}`}
                        className="ml-auto text-muted-foreground/70"
                        onClick={() => abrirNovo(dia, filtro || undefined)}
                      >
                        <Plus />
                      </Button>
                    </Hint>
                  </header>

                  <div className="scrollbar-hover min-h-0 flex-1 space-y-2 overflow-y-auto px-2 pb-2">
                    {itens.length === 0 ? (
                      <button
                        type="button"
                        onClick={() => abrirNovo(dia, filtro || undefined)}
                        className="flex h-20 w-full cursor-pointer items-center justify-center rounded-xl border border-dashed border-border/80 text-xs text-muted-foreground transition-colors hover:border-primary/60 hover:bg-primary/5 hover:text-primary"
                      >
                        Livre
                      </button>
                    ) : (
                      itens.map((a) => (
                        <AppointmentCard key={a.id} appointment={a} compact {...acoes} />
                      ))
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      )}

      <NewAppointmentDialog
        open={novo.open}
        onOpenChange={(open) => setNovo((v) => ({ ...v, open }))}
        professionals={professionals.data ?? []}
        initial={{
          professionalId: novo.professionalId,
          date: novo.date,
          time: novo.time,
        }}
      />

      <AppointmentDetailDialog
        appointment={aberto}
        onOpenChange={(open) => !open && setAberto(null)}
        onReschedule={(a) => {
          setAberto(null);
          setRemarcando(a);
        }}
      />

      <RescheduleAppointmentDialog
        appointment={remarcando}
        onOpenChange={(open) => !open && setRemarcando(null)}
        pending={remarcar.isPending}
        onConfirm={(scheduledAt) =>
          remarcando && remarcar.mutate({ id: remarcando.id, scheduledAt })
        }
      />
    </div>
  );
}

type AcoesDoCartao = {
  onOpen: (a: Appointment) => void;
  onReschedule: (a: Appointment) => void;
  onTransition: (a: Appointment, to: Transicao) => void;
};

/** Menu do cartão: faz por toque o que a ficha faz por botão, menos cancelar. */
function AppointmentMenu({
  appointment: a,
  nome,
  onOpen,
  onReschedule,
  onTransition,
  className,
}: AcoesDoCartao & { appointment: Appointment; nome: string; className?: string }) {
  const encerrado = appointmentEncerrado(a.status);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label={`Ações de ${nome}`}
          className={cn("text-muted-foreground data-[state=open]:bg-muted", className)}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onSelect={() => onOpen(a)}>
          <CalendarDays />
          Abrir ficha
        </DropdownMenuItem>
        {a.leadId && (
          <DropdownMenuItem asChild>
            <Link to="/leads/$leadId" params={{ leadId: a.leadId }}>
              <User />
              Ver lead
            </Link>
          </DropdownMenuItem>
        )}
        {!encerrado && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Situação</DropdownMenuLabel>
            {a.status === "scheduled" && (
              <DropdownMenuItem onSelect={() => onTransition(a, "confirmed")}>
                <Check />
                Confirmar
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={() => onTransition(a, "arrived")}>
              <DoorOpen />
              Cliente chegou
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onTransition(a, "completed")}>
              <ClipboardCheck />
              Concluir atendimento
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onTransition(a, "no_show")}>
              <UserX />
              Não compareceu
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onReschedule(a)}>
              <CalendarClock />
              Remarcar…
            </DropdownMenuItem>
            <DropdownMenuItem variant="destructive" onSelect={() => onOpen(a)}>
              <Ban />
              Cancelar horário…
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Cartão de agendamento.
 *
 * Na lista do dia: faixa de hora, cliente, serviço, profissional, status e
 * menu, numa linha. Na semana (`compact`): o mesmo em três linhas, para
 * caber numa coluna estreita. O cartão inteiro abre a ficha; o nome é um
 * botão para quem navega pelo teclado.
 */
function AppointmentCard({
  appointment: a,
  compact,
  ...acoes
}: AcoesDoCartao & { appointment: Appointment; compact?: boolean }) {
  const morto = a.status === "cancelled" || a.status === "rescheduled";
  const nome = a.contact.fullName ?? "Sem nome";
  const abrir = () => acoes.onOpen(a);

  const profissional = a.professional ? (
    <Hint label={a.professional.name} side="left">
      <span>
        <Avatar name={a.professional.name} size="xs" />
      </span>
    </Hint>
  ) : (
    <Hint label="Sem profissional" side="left">
      <span className="inline-flex size-6 items-center justify-center rounded-full border border-dashed text-muted-foreground">
        <User className="size-3" />
      </span>
    </Hint>
  );

  const nomeBotao = (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        abrir();
      }}
      className={cn(
        "block max-w-full truncate text-left font-semibold leading-tight hover:text-primary focus-visible:outline-none focus-visible:underline",
        compact ? "text-[13px]" : "text-sm",
        morto && "line-through",
      )}
    >
      {nome}
    </button>
  );

  if (compact) {
    return (
      <article
        onClick={abrir}
        className={cn(
          "group cursor-pointer rounded-xl border bg-card p-2.5 shadow-card transition-[box-shadow,transform,opacity] duration-150 hover:-translate-y-px hover:shadow-panel",
          morto && "opacity-60",
        )}
      >
        <div className="flex items-center gap-2">
          <span
            className={cn("kpi rounded-md px-1.5 py-0.5 text-xs font-semibold", FAIXA[a.status])}
          >
            {horaCurta(a.scheduledAt)}
          </span>
          <span className="text-[10px] tabular-nums text-muted-foreground">
            {a.durationMinutes} min
          </span>
          <AppointmentMenu appointment={a} nome={nome} className="-mr-1 ml-auto" {...acoes} />
        </div>
        <div className="mt-1.5">{nomeBotao}</div>
        <p className="truncate text-[11px] text-muted-foreground">{a.title}</p>
        <div className="mt-2 flex items-center justify-between gap-2">
          <AppointmentStatusBadge status={a.status} />
          {profissional}
        </div>
      </article>
    );
  }

  return (
    <article
      onClick={abrir}
      className={cn(
        "group flex cursor-pointer items-center gap-3 rounded-xl border bg-card p-2.5 shadow-card transition-[box-shadow,transform,opacity] duration-150 hover:-translate-y-px hover:shadow-panel sm:p-3",
        morto && "opacity-60",
      )}
    >
      {/* Faixa de hora */}
      <div
        className={cn(
          "flex w-[3.75rem] shrink-0 flex-col items-center justify-center self-stretch rounded-lg py-1.5",
          FAIXA[a.status],
        )}
      >
        <span className="kpi text-sm font-semibold leading-none">{horaCurta(a.scheduledAt)}</span>
        <span className="mt-1 text-[10px] tabular-nums opacity-75">{a.durationMinutes} min</span>
      </div>

      <Avatar
        name={a.contact.fullName}
        seed={a.contact.phoneNormalized ?? a.contact.id}
        size="md"
        className="hidden sm:inline-flex"
      />

      <div className="min-w-0 flex-1">
        {nomeBotao}
        <p className="truncate text-xs text-muted-foreground">
          {a.title}
          {a.price ? ` · ${formatCurrency(a.price)}` : ""}
        </p>
        <div className="mt-1 sm:hidden">
          <AppointmentStatusBadge status={a.status} />
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {profissional}
        <AppointmentStatusBadge status={a.status} className="hidden sm:inline-flex" />
        <AppointmentMenu appointment={a} nome={nome} className="-mr-1" {...acoes} />
      </div>
    </article>
  );
}
